import React, { useState } from 'react';
import realTimeDeviceService from '../../services/realTimeDeviceService';
import '../assets/ActiveDebugSessions.css';

/**
 * ActiveDebugSessions — "Active Debug Sessions on This Device" list.
 *
 * Props:
 *   rdSessions      {Array}    — normalized RD session rows from useDeviceActiveSessions
 *   rtSessions      {Array}    — normalized RT session rows
 *   onViewRd        {function} — called to navigate to RD Session Manager (View tab)
 *   onViewRt        {function} — called to navigate to RT Session Manager (View tab)
 *   refresh         {function} — re-fetch sessions (called after stop)
 *
 * Returns null when there are no sessions to show.
 */
function ActiveDebugSessions({ rdSessions = [], rtSessions = [], onViewRd, onViewRt, refresh }) {
  const [stoppingId, setStoppingId] = useState(null);

  const allSessions = [...rdSessions, ...rtSessions];
  if (allSessions.length === 0) return null;

  const handleStop = async (session) => {
    if (!session.sessionId) return;
    setStoppingId(session.sessionId);
    try {
      await realTimeDeviceService.stopSession(session.sessionId, session.systemId);
    } catch (_) {}
    setStoppingId(null);
    if (refresh) refresh();
  };

  return (
    <div className="ads-root">
      <div className="ads-header">
        <span className="ads-title">🟢 Active Debug Sessions on This Device</span>
        <span className="ads-subtitle">Sessions launched via Debug Strategies — also visible in the RD/RT Session Managers.</span>
      </div>
      <div className="ads-list">
        {allSessions.map((session, idx) => {
          const isRd = session.kind === 'rd';
          const isStopping = stoppingId === session.sessionId;
          const onView = isRd ? onViewRd : onViewRt;
          const managerLabel = isRd ? 'View in RD Session Manager →' : 'View in RT Session Manager →';

          return (
            <div key={session.sessionId || `${session.kind}-${idx}`} className="ads-row">
              <div className="ads-row-main">
                <span className={`ads-kind-chip ads-kind-chip--${session.kind}`}>
                  {isRd ? 'RD' : 'RT'}
                </span>
                <div className="ads-row-info">
                  <span className="ads-strategy-label">{session.strategyLabel}</span>
                  {session.startedLabel && (
                    <span className="ads-started">Started {session.startedLabel}</span>
                  )}
                </div>
              </div>
              <div className="ads-row-actions">
                <span className="ads-live-badge">
                  <span className="ads-live-dot" />
                  Active
                </span>
                {onView && (
                  <button className="ads-mgr-link" onClick={onView}>
                    {managerLabel}
                  </button>
                )}
                {!isRd && (
                  <button
                    className="ads-stop-btn"
                    title="Stop session"
                    disabled={isStopping}
                    onClick={() => handleStop(session)}
                  >
                    {isStopping ? '…' : '✕'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ActiveDebugSessions;
